import { config } from '@psl/functions/config';
import { WithID } from '@psl/models/ClientModels';
import { Platform } from 'react-native';
import {
  combineLatest,
  concatWith,
  defer,
  Observable,
  of,
  OperatorFunction,
  skipWhile,
  switchMap,
} from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { shareReplay } from '../../utils/rxExtensions';
import { safeMarkDevFlag } from '../../utils/safeMarkDevFlag';
import { isNotNull } from '../../utils/utils';
import type {
  DocumentReference,
  DocumentSnapshot,
  Query,
  QueryDocumentSnapshot,
  QuerySnapshot,
} from './firebaseAccess';

export type { WithID };

const DEBUG_FIRESTORE_USAGE = safeMarkDevFlag(false);

function logQuerySnapshot(query: Query<any>, snapshot: QuerySnapshot<any>) {
  if (!DEBUG_FIRESTORE_USAGE) {
    return;
  }
  const path = (query as any)._collectionPath?._parts?.join('/');
  const modifier = (query as any)._modifiers?._filters
    ?.map((f: any) => f?.fieldPath?._segments.join(','))
    ?.join('&');
  console.log(`Query: ${path}?${modifier}: ${snapshot.size}`);
}

export function collectionQuery<T>(
  query: Query<T>,
  includeMetadataChanges = false,
) {
  let _query: any;

  if ((__DEV__ || !config.isProd) && Platform.OS === 'web') {
    const obj = query as any;
    _query = obj._delegate?._query;
    if (_query?.path == null) {
      console.log('_firestore query: ', _query);
      throw new Error('Query path is null: ' + JSON.stringify(_query));
    }
  }
  return new Observable<QuerySnapshot<T>>(subscriber => {
    const sub = query.onSnapshot(
      { includeMetadataChanges },
      {
        next: snapshot => {
          subscriber.next(snapshot);
          logQuerySnapshot(query, snapshot);
        },
        error: error => {
          let path = (query as any).path;
          try {
            if (_query) {
              path = JSON.stringify(_query);
            }
          } catch (e) {
            console.error(e);
          }
          const msg = `Firestore query failed: ${error.message}, query: ${path}`;
          console.error(msg, error);
          const err = new Error(msg);
          err.cause = error;
          subscriber.error(err);
        },
        complete: () => {
          subscriber.complete();
        },
      },
    );
    return { unsubscribe: sub };
  });
}

export const snapshotToDataWithId = <T>(
  snapshot: DocumentSnapshot<T> | null | undefined,
): null | WithID<T> => {
  return snapshot != null && snapshot.exists
    ? { id: snapshot.id, ...snapshot.data()! }
    : null;
};

export const snapshotsToDataArrayWithId = <T>(
  snapshots: DocumentSnapshot<T>[],
): WithID<T>[] => {
  return snapshots.map(snapshotToDataWithId).filter(isNotNull);
};

function querySnapshotToDataWithId<T>(snapshot: QuerySnapshot<T>): WithID<T>[] {
  return snapshot.docs.map(d => snapshotToDataWithId(d) as any);
}

export function collectionDataWithId<T>(
  query: Query<T>,
): Observable<WithID<T>[]> {
  return collectionQuery(query).pipe(map(querySnapshotToDataWithId));
}

export function collectionDataWithIdWithoutCache<T>(
  query: Query<T>,
): Observable<WithID<T>[]> {
  return collectionQuery(query, true).pipe(
    filter(sp => !sp.metadata.fromCache),
    map(querySnapshotToDataWithId),
  );
}

export function docDataWithId<T>(
  ref: DocumentReference<T>,
): Observable<WithID<T>> {
  return docQuery(ref).pipe(map(sp => snapshotToDataWithId(sp) as any));
}

export function docDataWithIdOrNull<T>(
  ref: DocumentReference<T>,
): Observable<WithID<T> | null> {
  return docQueryOrNull(ref).pipe(map(sp => snapshotToDataWithId(sp) as any));
}

export function docDataWithIdMaybe<T>(
  ref: DocumentReference<T>,
): Observable<null | WithID<T>> {
  return docQuery(ref).pipe(map(sp => snapshotToDataWithId(sp)));
}

export function collectionDoc<T>(
  query: Query<T>,
): Observable<QueryDocumentSnapshot<T>[]> {
  return collectionQuery(query).pipe(map(qs => qs.docs));
}

function logDocumentSnapshot(snapshot: DocumentSnapshot<any>) {
  if (!DEBUG_FIRESTORE_USAGE) {
    return;
  }
  console.log(`Doc: ${snapshot.ref.path} updated`);
}

export function docQuery<T>(
  doc: DocumentReference<T>,
  includeMetadataChanges = false,
): Observable<DocumentSnapshot<T>> {
  return new Observable<DocumentSnapshot<T>>(subscriber => {
    const sub = doc.onSnapshot(
      { includeMetadataChanges },
      {
        next: snapshot => {
          subscriber.next(snapshot);
          logDocumentSnapshot(snapshot);
        },
        error: error => {
          const msg = `Firestore document query ${doc.path} failed: ${error.message}`;
          console.error(msg, error);
          const err = new Error(msg);
          err.cause = error;
          subscriber.error(err);
        },
        complete: () => {
          subscriber.complete();
        },
      },
    );
    return { unsubscribe: sub };
  });
}

export function docQueryOrNull<T>(
  doc: DocumentReference<T>,
  includeMetadataChanges = false,
): Observable<DocumentSnapshot<T> | null> {
  return new Observable<DocumentSnapshot<T> | null>(subscriber => {
    const sub = doc.onSnapshot(
      { includeMetadataChanges },
      {
        next: snapshot => {
          subscriber.next(snapshot);
          logDocumentSnapshot(snapshot);
        },
        error: error => {
          const msg = `Firestore document query ${doc.path} failed: ${error.message}`;
          console.error(msg, error);
          subscriber.next(null);
          subscriber.complete();
        },
        complete: () => {
          subscriber.complete();
        },
      },
    );
    return { unsubscribe: sub };
  });
}

export function docWithoutCache<T>(d: DocumentReference<T>) {
  return docQuery(d, true).pipe(filter(sp => !sp.metadata.fromCache));
}

export const memorize = <T extends string | number, U>(
  func: (input: T) => U,
) => {
  const caches = new Map<T, U>();
  return (key: T): U => {
    if (caches.has(key)) {
      return caches.get(key)!;
    }
    const result = func(key);
    caches.set(key, result);
    return result;
  };
};

export const debug = <T>(id?: string, showValue = true) =>
  tap<T>({
    next: value =>
      console.log(`${id ?? 'Untitled'} Next: `, showValue ? value : ''),
    error: error => console.log(`${id ?? 'Untitled'} Error: `, error),
    complete: () => console.log(`${id ?? 'Untitled'} Completed`),
  });

export function combineLatestFrom<T, R, U>(
  from: Observable<R>,
  mapping: (source: T, from: R) => U,
) {
  return (input: Observable<T>): Observable<U> =>
    combineLatest([input, from]).pipe(map(([l, r]) => mapping(l, r)));
}

export function filterWith<T>(
  filter: Observable<(i: T) => boolean>,
): (input: Observable<T[]>) => Observable<T[]> {
  return input =>
    combineLatest([input, filter]).pipe(map(([items, f]) => items.filter(f)));
}

export function combineLatestArray<T>(input: Observable<T>[]) {
  return input.length === 0 ? of([]) : combineLatest(input);
}

export type ObservableQuery<T1, T2 = DocumentSnapshot<T1>> = (
  queryModifier?: null | ((query: Query<T1>) => Query<T1>),
  options?: ObservableQuery.QueryOptions,
) => Observable<ObservableQuery.QueryResult<T1, T2>>;
export namespace ObservableQuery {
  export interface QueryOptions {
    /**
     * @default cache
     */
    source?: 'cache' | 'server';
  }

  export interface QueryResult<T1, T2 = DocumentSnapshot<T1>> {
    querySnapshot: QuerySnapshot<T1>;
    docs: T2[];
  }
}

export function createObservableQuery<T>(
  query: Query<T>,
  observableOperator?: OperatorFunction<
    QuerySnapshot<T>,
    DocumentSnapshot<T>[]
  >,
): ObservableQuery<T, DocumentSnapshot<T>>;
export function createObservableQuery<T1, T2 = DocumentSnapshot<T1>>(
  query: Query<T1>,
  observableOperator: OperatorFunction<QuerySnapshot<T1>, T2[]>,
): ObservableQuery<T1, T2>;
export function createObservableQuery<T1, T2>(
  query: Query<T1>,
  observableOperator?: OperatorFunction<QuerySnapshot<T1>, T2[]>,
): ObservableQuery<T1, T2> {
  const operator =
    observableOperator ?? map((snap: QuerySnapshot<T1>) => snap.docs as any[]);

  return (queryModifier, options = {}) => {
    const finalQuery = queryModifier == null ? query : queryModifier(query);

    const wrappedObservableOperator = (
      ob: Observable<QuerySnapshot<T1>>,
    ): Observable<ObservableQuery.QueryResult<T1, T2>> =>
      ob.pipe(
        shareReplay(1),
        switchMap(querySnap =>
          of(querySnap).pipe(
            operator,
            map(docs => ({ querySnapshot: querySnap, docs })),
          ),
        ),
      );

    if (options.source !== 'server') {
      return collectionQuery(finalQuery).pipe(wrappedObservableOperator);
    }

    return defer(() => finalQuery.get()).pipe(
      concatWith(
        collectionQuery(finalQuery).pipe(
          skipWhile((res, idx) => idx === 0 && res.metadata.fromCache),
        ),
      ),
      wrappedObservableOperator,
    );
  };
}

export type WrappedObservableQuery<T1, T2> = (
  queryModifier?: null | ((query: Query<T1>) => Query<T1>),
  options?: ObservableQuery.QueryOptions,
) => Observable<T2>;
export function wrapObservableQuery<T1, T2, T3>(
  query: ObservableQuery<T1, T2>,
  wrapperOperator: OperatorFunction<ObservableQuery.QueryResult<T1, T2>, T3>,
): WrappedObservableQuery<T1, T3> {
  return (...args) => query(...args).pipe(wrapperOperator);
}
